import { supabase } from '@/lib/supabase'
import type { Plan } from '../types'
import { getAccountAccessState, type AccountAccessState } from './accessStateService'
import { normalizePlan } from '../utils/normalizePlan'

export async function listActivePlans(): Promise<Plan[]> {
  // Member view: apenas planos ativos e visíveis
  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .eq('active', true)
    .order('created_at', { ascending: true })
  if (error) throw error
  const items = (data as any[] | null) ?? []
  return items.map(normalizePlan).filter((p) => p.visible)
}

export async function listPublicPlans(): Promise<Plan[]> {
  // Público (Home/landing): mostrar apenas planos ativos e visíveis
  // Observação: alguns ambientes não possuem coluna `visible`; o flag pode estar em `features.visible`.
  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .eq('active', true)
    .order('created_at', { ascending: true })
  if (error) throw error
  const items = (data as any[] | null) ?? []
  return items.map(normalizePlan).filter((p) => p.visible)
}

// Lista planos ativos sem filtrar por visibilidade (para uso em modos especiais)
export async function listActivePlansIncludingInvisible(): Promise<Plan[]> {
  const { data, error } = await supabase
    .from('plans')
    .select('*')
    .eq('active', true)
    .order('created_at', { ascending: true })
  if (error) throw error
  const items = (data as any[] | null) ?? []
  return items.map(normalizePlan)
}

function normalizeId(value: unknown): string {
  return String(value || '').trim()
}

function collectDistinctIds(values: unknown[]): string[] {
  return Array.from(
    new Set(values.map(normalizeId).filter(Boolean)),
  )
}

// SSOT de elegibilidade para áreas de membro (my-plans/checkout):
// - sempre inclui planos publicos (visible=true)
// - inclui planos ativos invisiveis que o usuario ja teve (subscriptions.plan_id)
// - inclui fallback de downgrade por override (account_plan_overrides.original_plan_id)
export async function listMemberEligiblePlansForUser(userId?: string | null): Promise<Plan[]> {
  const all = await listActivePlansIncludingInvisible()
  const publicPlans = all.filter((p) => p.visible)

  const uid = normalizeId(userId)
  if (!uid) return publicPlans

  const { data: links, error: linksErr } = await supabase
    .from('account_users')
    .select('account_id')
    .eq('user_id', uid)
  if (linksErr) throw linksErr

  const accountIds = collectDistinctIds(((links || []) as any[]).map((r) => r.account_id))
  if (accountIds.length === 0) return publicPlans

  const [{ data: subs, error: subsErr }, { data: overrides, error: overridesErr }] = await Promise.all([
    supabase
      .from('subscriptions')
      .select('plan_id')
      .in('account_id', accountIds)
      .not('plan_id', 'is', null),
    supabase
      .from('account_plan_overrides')
      .select('original_plan_id')
      .in('account_id', accountIds)
      .not('original_plan_id', 'is', null),
  ])
  if (subsErr) throw subsErr
  if (overridesErr) throw overridesErr

  const allowedIds = new Set<string>([
    ...collectDistinctIds(((subs || []) as any[]).map((s) => s.plan_id)),
    ...collectDistinctIds(((overrides || []) as any[]).map((o) => o.original_plan_id)),
  ])
  if (allowedIds.size === 0) return publicPlans

  const merged = all.filter((p) => p.visible || allowedIds.has(p.id))

  // Ordem estável baseada na ordenacao original de `all`
  const seen = new Set<string>()
  const result: Plan[] = []
  for (const p of merged) {
    if (seen.has(p.id)) continue
    seen.add(p.id)
    result.push(p)
  }
  return result
}

export async function getCurrentPlanForUser(
  userId: string,
): Promise<{ plan: Plan | null; subscription: any | null; access: AccountAccessState | null }> {
  if (!userId) return { plan: null, subscription: null, access: null }

  let access: AccountAccessState | null = null
  try {
    access = await getAccountAccessState()
  } catch {}

  if (access) {
    const planId = access.effectivePlanId || null
    const subscription = access.subscriptionStatus
      ? {
          status: access.subscriptionStatus,
          current_period_end: access.subscriptionCurrentPeriodEnd,
          current_period_start: access.subscriptionCurrentPeriodStart,
          stripe_subscription_id: access.stripeSubscriptionId,
          is_trial: access.subscriptionIsTrial,
          trial_ends_at: access.subscriptionTrialEndsAt,
          plan_id: access.subscriptionPlanId,
        }
      : null
    if (planId) {
      const { data: prow } = await supabase.from('plans').select('*').eq('id', planId).maybeSingle()
      const plan = prow ? normalizePlan(prow) : null
      return { plan, subscription, access }
    }
    return { plan: null, subscription, access }
  }

  // Fallback: resolve account for this user
  const { data: memberships, error: memErr } = await supabase
    .from('account_users')
    .select('account_id, role')
    .eq('user_id', userId)
  if (memErr) throw memErr
  const membership = (memberships || [])[0]
  if (!membership) return { plan: null, subscription: null, access: null }

  // Pick best/latest subscription for the account
  const { data: subs, error: subsErr } = await supabase
    .from('subscriptions')
    .select(
      'id, account_id, status, plan_id, current_period_end, current_period_start, stripe_subscription_id, created_at, is_trial, trial_ends_at',
    )
    .eq('account_id', membership.account_id)
    .order('created_at', { ascending: false })
    .limit(10)
  if (subsErr) throw subsErr

  const rank = (st: string | null | undefined) => (st === 'active' ? 3 : st === 'trialing' ? 2 : st === 'past_due' ? 1 : 0)
  let chosen: any = null
  for (const s of subs || []) {
    if (!chosen) chosen = s
    else {
      const better =
        rank(s.status) > rank(chosen.status) ||
        (rank(s.status) === rank(chosen.status) &&
          ((s.current_period_end || s.created_at || '') > (chosen.current_period_end || chosen.created_at || '')))
      if (better) chosen = s
    }
  }
  if (!chosen?.plan_id) return { plan: null, subscription: chosen, access: null }

  const { data: prow } = await supabase.from('plans').select('*').eq('id', chosen.plan_id).maybeSingle()
  const plan = prow ? normalizePlan(prow) : null
  return { plan, subscription: chosen, access: null }
}

type StripeSyncCandidateRow = {
  stripe_subscription_id?: string | null
  status?: string | null
  current_period_end?: string | null
  created_at?: string | null
}

function rankStripeSyncCandidateStatus(status?: string | null): number {
  const st = String(status || '').toLowerCase()
  if (st === 'active') return 6
  if (st === 'trialing') return 5
  if (st === 'past_due') return 4
  if (st === 'unpaid') return 3
  if (st === 'incomplete') return 2
  if (st === 'canceled') return 1
  if (st === 'incomplete_expired') return 0
  return 0
}

function candidateRecency(row: StripeSyncCandidateRow): string {
  return String(row.current_period_end || row.created_at || '')
}

export async function getLatestStripeSubscriptionSyncCandidate(userId: string): Promise<string | null> {
  const uid = normalizeId(userId)
  if (!uid) return null
  try {
    const { data: memberships, error: memErr } = await supabase
      .from('account_users')
      .select('account_id')
      .eq('user_id', uid)
      .order('created_at', { ascending: true })
      .limit(1)
    if (memErr) throw memErr
    const accountId = (memberships || [])[0]?.account_id || null
    if (!accountId) return null

    const { data: rows, error: rowsErr } = await supabase
      .from('subscriptions')
      .select('stripe_subscription_id, status, current_period_end, created_at')
      .eq('account_id', accountId)
      .not('stripe_subscription_id', 'is', null)
      .order('created_at', { ascending: false })
      .limit(20)
    if (rowsErr) throw rowsErr

    let best: StripeSyncCandidateRow | null = null
    for (const row of (rows || []) as StripeSyncCandidateRow[]) {
      const sid = String(row?.stripe_subscription_id || '').trim()
      if (!sid) continue
      if (!best) {
        best = row
        continue
      }
      const rb = rankStripeSyncCandidateStatus(row.status)
      const rc = rankStripeSyncCandidateStatus(best.status)
      if (rb > rc) {
        best = row
        continue
      }
      if (rb === rc && candidateRecency(row) > candidateRecency(best)) {
        best = row
      }
    }

    return best ? String(best.stripe_subscription_id || '').trim() || null : null
  } catch {
    return null
  }
}

// Planos disponiveis no checkout para um usuario especifico:
// - usa a mesma SSOT de elegibilidade aplicada em my-plans
export async function listCheckoutPlansForUser(userId?: string | null): Promise<Plan[]> {
  return listMemberEligiblePlansForUser(userId)
}

export async function getPlanByRef(ref: string): Promise<Plan | null> {
  const key = (ref || '').trim()
  if (!key) return null
  // Try by id first (evita 400 quando a coluna slug não existe), depois por slug
  try {
    const byId = await supabase.from('plans').select('*').eq('id', key).maybeSingle()
    if (!byId.error && byId.data) return normalizePlan(byId.data)
  } catch {}
  try {
    const bySlug = await supabase.from('plans').select('*').eq('slug', key).maybeSingle()
    if (!bySlug.error && bySlug.data) return normalizePlan(bySlug.data)
  } catch {}
  return null
}

export async function previewChangeSubscriptionPlan(targetPlanRef: string, label?: string): Promise<any> {
  const { data, error } = await supabase.functions.invoke('change-subscription-plan', {
    body: { plan: targetPlanRef, label, confirm: false },
  })
  if (error) throw error
  const payload = Array.isArray(data) ? (data as any)[0] : (data as any)
  if (payload?.error) throw new Error(payload.error)
  return payload
}

export async function applyChangeSubscriptionPlan(
  targetPlanRef: string,
  label?: string,
): Promise<{
  mode: 'upgrade' | 'downgrade' | 'noop'
  client_secret?: string | null
  publishable?: string | null
  scheduled_for?: string | null
  subscription_id?: string | null
}> {
  const { data, error } = await supabase.functions.invoke('change-subscription-plan', {
    body: { plan: targetPlanRef, label, confirm: true },
  })
  if (error) throw error
  const payload = Array.isArray(data) ? (data as any)[0] : (data as any)
  if (payload?.error) throw new Error(payload.error)
  if (payload?.message === 'already_on_plan')
    return { mode: 'noop', client_secret: null, publishable: payload?.publishable ?? null, scheduled_for: null }
  return {
    mode: (payload?.mode as any) || 'noop',
    client_secret: payload?.client_secret ?? null,
    publishable: payload?.publishable ?? null,
    scheduled_for: payload?.scheduled_for ?? null,
    subscription_id: payload?.subscription_id ?? null,
  }
}

export type NextCycleInfo = {
  current_period_end: string | null
  scheduled_change?: { id: string; name: string } | null
  vip_discount_percent?: number
  vip_free?: boolean
  next_amount_due_cents?: number | null
  next_subtotal_cents?: number | null
  next_currency?: string | null
}

export async function getNextCycleInfo(): Promise<NextCycleInfo> {
  const { data, error } = await supabase.functions.invoke('get-subscription-next', { body: {} as any })
  if (error) throw error
  const payload = Array.isArray(data) ? (data as any)[0] : (data as any)
  return {
    current_period_end: payload?.current_period_end || null,
    scheduled_change: payload?.scheduled_change || null,
    vip_discount_percent: payload?.vip_discount_percent || 0,
    vip_free: !!payload?.vip_free,
    next_amount_due_cents:
      typeof payload?.next_amount_due_cents === 'number' ? payload.next_amount_due_cents : null,
    next_subtotal_cents:
      typeof payload?.next_subtotal_cents === 'number' ? payload.next_subtotal_cents : null,
    next_currency: payload?.next_currency || null,
  }
}

export async function cancelSubscriptionAtPeriodEnd(): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke('cancel-subscription', { body: {} as any })
  if (error) throw error
  const payload = Array.isArray(data) ? (data as any)[0] : (data as any)
  return payload?.ends_at || null
}

export async function cancelScheduledChange(): Promise<boolean> {
  const { data, error } = await supabase.functions.invoke('cancel-scheduled-change', { body: {} as any })
  if (error) throw error
  const payload = Array.isArray(data) ? (data as any)[0] : (data as any)
  if (payload?.error) throw new Error(payload.error)
  return !!payload?.ok
}

export async function syncSubscriptionStatus(subscription_id: string): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke('sync-subscription-status', {
    body: { subscription_id } as any,
  })
  if (error) throw error
  const payload = Array.isArray(data) ? (data as any)[0] : (data as any)
  return payload?.status || null
}
